import HeroBasic from '../components/HeroBasic'
import Card from '../components/Card'
import List from '../components/List'

function AboutData() {
    var heroTitle = "Data Info & Caveats"
    var heroSubtitle = "FOIA scope, missing records, and limitations."

    var cardHeader = "Data Scope"

    var dataScopes = [
        "Documents Provided to PEER by EPA via FOIA",
        "EPA Region 6 Documents from Region 6 Portal or ECHO",
        "Documents online in ECHO (non-Region 6)"
    ]

    var sources = [
        "Inspection reports",
        "Notices of violation",
    ]

    var documentTypes = [
        "INSP - Inspection Reports",
        "NOV - Notices of Violation(Informal Actions)",
    ]

    var statutes = [ 
        "CAA – Clean Air Act",
        "CWA – Clean Water Act",
        "RCRA – Resource Conservation and Recovery Act",
    ]

    var regions = [ 
        "1 - 10",
        "HQ",
    ]

    return (
        <div>
            <HeroBasic title={heroTitle} subtitle={heroSubtitle} />
            <Card header={cardHeader}>
                <List items={dataScopes} />

                <h2>FOIA Scope</h2>
                <List items={sources} />
                <p>
                        The original PEER FOIA covered the period of January 20, 2021 and February 4, 2025 for any enforcement actions that have not been resolved through a final administrative or judicial consent decree or judgment, or by a final administrative order: 
                </p>
                <p>
                    1. All judicial consent decrees that have been lodged but not yet entered by a federal court; 
                </p>
                <p>
                    2. All Administrative Orders that have not been finalized, e.g., through an Administrative Order on Consent;
                </p>
                <p>
                    3. All Notices of Violation or Findings of Violations;
                </p>
                <p>
                    4. All information requests issued pursuant to EPA's enforcement authority;
                </p>
                <p>
                    5. All inspection reports; and
                </p>
                <p>
                    6. All indictments in cases involving criminal violations of environmental law.
                </p>

                <p>
                    EPA Regional offices handle most of the inspection and enforcement activities. Regions provided responses directly to PEER. Some Regions had trouble producing all the documents in a timely manner, so for those, PEER narrowed the timing (starting in 2022), the statutes (CAA, CWA, RCRA), and the document types (inspection reports and notices of violation). The Eyes on Environmental Enforcement Portal includes responsive documents from that FOIA. PEER is waiting for more documents that have not been provided in a timely manner. EPA has not completely fulfilled its obligations to submit all documents and as a result, PEER has ongoing litigation related to EPA’s FOIA response. Especially troubling is that EPA does not have these documents automated in a central system – meaning it does not have the capability to easily mine through them to find common problems.  
                </p>
                <p>
                    Because of EPA’s problems gathering these documents, PEER also narrowed its original request to expedite receiving the documents. The narrowing was focused on CAA, CWA and RCRA. The primary documents included are inspection reports and notices of violation (informal actions).  
                </p>
                <p>
                    PEER will be continually adding documents as they are received and is developing a new FOIA request to obtain more recent documentation. PEER is disappointed that EPA has not posted these documents online. EPA has a document repository within the Enforcement and Compliance History Online (ECHO) website; however, the Office of Enforcement and Compliance Assurance (OECA) has not mandated that the Regional offices enter their enforcement documents into this platform. EPA’s lack of digitization is a key reason why this Portal is needed. 
                </p>
                <p>
                    The Eyes on Environmental Enforcement Search Portal includes other documents beyond what was provided via FOIA. EPA Region 6 has a searchable online portal with documents for Arkansas, Oklahoma, Texas, Louisiana, New Mexico. This portal is a good model that other Regions are choosing not to use. Because Region 6 data are online, EPA did not need to send documents to PEER via the laborious FOIA process. Instead, PEER took the files from ECHO and Region 6 and added them to the documents received via FOIA from other Regions. As noted above, ECHO also has a document upload capability, but most Regions are not using it. PEER has included those ECHO documents as well. 
                </p>

                <h2>Available Search Features</h2>

                <h3>Year</h3>

                <ul>
                    <li>
                        PEER is only including documents from 2022 on.  
                        <ul>
                            <li>
                                    PEER has not yet requested documents after the initial FOIA (February 2025), so the EoEE portal does not have FOIA documents after February 2025 (unless a Region provided a late response that had newer documents). 
                            </li>
                        </ul>
                    </li>
                    <li>
                        EPA provided information about Region 6 and ECHO document data on May 4, 2026. 
                        <ul>
                            <li>
                                There may be a limited amount of additional data after May 4, 2026 if PEER was able to find that independently. 
                            </li>
                        </ul>
                    </li>
                </ul>

                <h3>Document Types</h3>
                <List items={documentTypes} />

                <h3>Statutes</h3>
                <List items={statutes} />

                <h3>Region</h3>
                <List items={regions} />

                <h3>Companies and Entities</h3>
                <ul>
                    <li>
                        The indexed organizational names from each document. 
                    </li>
                </ul>

                <h2>Advanced User Notes</h2>
                <ul>
                    <li>
                            PEER currently is not including enforcement documents. However, EPA does have a search page that includes administrative orders. That webpage can be useful in tracking down whether a notice of violation turned into an administrative order (formal enforcement). See - <a href="https://yosemite.epa.gov/oa/rhc/epaadmin.nsf/CEC+Dockets+by+Year+Filed?OpenView&RestrictToCategory=2025&Start=1">https://yosemite.epa.gov/oa/rhc/epaadmin.nsf/CEC+Dockets+by+Year+Filed?OpenView&RestrictToCategory=2025&Start=1</a> 
                    </li>
                    <li>
                            Users can search ECHO to find whether enforcement actions were taken.  
                            <ul>
                                <li>
                                        EPA’s FOIA documents do not reference EPA ID number, so a user would need to search ECHO for the facility name (and potentially state and city) to find the ECHO Detailed Facility Report. 
                                </li>
                                <li>
                                            EPA’s Region 6 and ECHO documents often include an ID number in the search return screen. That ID number can be pasted on the end of this URL to view an ECHO report for the same facility. That report should show whether enforcement follow up happened. 
                                </li>
                                <ul>
                                    <li>
                                            Enter the ID into echo.epa.gov in the Quick Search - Facility Name/ID Box, or 
                                    </li>
                                    <li>
                                            Paste the ID at the end of this URL: <a>https://echo.epa.gov/detailed-facility-report?fid=[[insert ID number]]</a>
                                    </li>
                                </ul>
                            </ul>
                    </li>
                    <li>
                            Users that would like to search online for newer Region 6 documents can do so one state at a time. For example, here is the website for Texas: <a href="https://www.epa.gov/tx/enforcement-compliance-assurance-documents-texas">https://www.epa.gov/tx/enforcement-compliance-assurance-documents-texas</a>
                    </li>
                </ul>

                <h2>Search Help</h2>
                <ul>
                    <li>
                            When keyword searching on the Portal page, you are searching on the shortened indexed information, not the full documents. 
                    </li>
                    <li>
                            PEER is planning future Advanced Search that will allow keyword searches of documents. 
                    </li>
                </ul>
            </Card>
        </div>
    )
}

export default AboutData